// The pure half of "Subir documentos" in an office's closing (B-100): which of the chosen files is
// which of the office's documents, said before anything is sent.
//
// The rules are the backend's (`listCierreOfficeFileRules`: the office email's own `attachments:`
// rules, only those whose documents this office's profile reads, one entry per document), so what
// the screen shows is what the backend will do: the first rule a file matches is its document, and a
// file no rule matches is kept beside the closing as an annex, unread. Names no client and no document.
//
// B-132 — a rule may match a workbook by its sheets (`sheets`, lower-cased) as well as by its name:
// the GLORY is "AMBALEMA 15.xlsx" as often as "GLORY AMBALEMA.xlsx", and its two sheets are what
// every copy has. A file matches an entry by either. The sheets are read in the browser
// (`hojasDeLibro`), before the screen classifies, and handed in as `hojasDe`.

// The backend takes this many files at once (`uploadCierreOfficeFiles`).
export const MAX_ARCHIVOS_OFICINA = 20;

/** What one entry is known by on the screen: its pattern, or its documents when it has none. */
export const claveDeRegla = (regla) => regla.name || (regla.documents || []).join(",");

const porNombre = (regla, nombre) => {
  if (!regla.name) return false;
  try {
    return new RegExp(regla.name, "i").test(String(nombre || ""));
  } catch (e) {
    return false;
  }
};

const porHojas = (regla, hojas) => Array.isArray(regla.sheets) && regla.sheets.length > 0
  && Array.isArray(hojas) && regla.sheets.every((hoja) => hojas.includes(String(hoja).toLowerCase()));

/**
 * The first rule the file matches — by its name, or (B-132) by the sheets it holds — or null. A
 * pattern that is not one is skipped. `hojas` are the workbook's sheet names, lower-cased, or null.
 */
export const reglaDeArchivo = (nombre, reglas, hojas = null) => (reglas || [])
  .find((regla) => porNombre(regla, nombre) || porHojas(regla, hojas)) || null;

const desescapar = (texto) =>
  texto.replace(/&lt;/g, "<").replace(/&gt;/g, ">").replace(/&quot;/g, '"').replace(/&apos;/g, "'").replace(/&amp;/g, "&");

/**
 * The sheet names of an `.xlsx` (its `xl/workbook.xml`) or an `.ods` (the `table:table` elements of
 * its `content.xml` — Cotorra's GLORY export, B-134), lower-cased, the way the backend's
 * `email/attachments.js` reads them; null for anything else or a file that does not open. Only a
 * rule with `sheets` needs them.
 */
export async function hojasDeLibro(archivo, { cargarJSZip = () => import("jszip").then((m) => m.default || m) } = {}) {
  const nombre = String(archivo?.name || "");
  const esOds = /\.ods$/i.test(nombre);
  if (!esOds && !/\.xlsx$/i.test(nombre)) return null;
  try {
    const JSZip = await cargarJSZip();
    const zip = await JSZip.loadAsync(typeof archivo.arrayBuffer === "function" ? await archivo.arrayBuffer() : archivo);
    const libro = zip.file(esOds ? "content.xml" : "xl/workbook.xml");
    if (!libro) return null;
    const xml = await libro.async("string");
    const patron = esOds ? /<table:table\b[^>]*\btable:name="([^"]*)"/g : /<(?:\w+:)?sheet\b[^>]*\bname="([^"]*)"/g;
    return [...xml.matchAll(patron)].map((m) => desescapar(m[1]).trim().toLowerCase());
  } catch (e) {
    return null;
  }
}

/**
 * The files onto the office's documents. A document receives one file; of two, the most recently
 * modified stays and the other is kept as `repetidos` — the backend would read both, one over the
 * other, and which one wins should not be a surprise.
 *
 * @returns {{ porRegla: Object<string, File>, anexos: File[], repetidos: File[] }}
 */
export const clasificarArchivos = (archivos, reglas, hojasDe = () => null) => {
  const porRegla = {};
  const anexos = [];
  const repetidos = [];
  Array.from(archivos || []).forEach((archivo) => {
    const regla = reglaDeArchivo(archivo.name, reglas, hojasDe(archivo));
    if (!regla) {
      anexos.push(archivo);
      return;
    }
    const clave = claveDeRegla(regla);
    const previo = porRegla[clave];
    if (!previo) {
      porRegla[clave] = archivo;
    } else if ((archivo.lastModified || 0) > (previo.lastModified || 0)) {
      porRegla[clave] = archivo;
      repetidos.push(previo);
    } else {
      repetidos.push(archivo);
    }
  });
  return { porRegla, anexos, repetidos };
};

/** The files to send: one per document, then the annexes — never a repeated one. */
export const archivosAEnviar = ({ porRegla, anexos }, reglas) => [
  ...(reglas || []).map((regla) => porRegla[claveDeRegla(regla)]).filter(Boolean),
  ...anexos,
];

/** Files added to the ones already chosen, by name: the same name again replaces nothing. */
export const sumarArchivos = (actuales, nuevos) => {
  const nombres = new Set((actuales || []).map((a) => a.name));
  return [...(actuales || []), ...Array.from(nuevos || []).filter((a) => !nombres.has(a.name))];
};
